import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Target, AlertTriangle, Check, TrendingDown } from "lucide-react";

const statusStyles = {
  ok: { bg: "rgba(45,106,79,0.08)", border: "rgba(45,106,79,0.15)", color: "#2D6A4F", label: "On Track" },
  warning: { bg: "rgba(231,111,81,0.08)", border: "rgba(231,111,81,0.15)", color: "#E76F51", label: "Warning" },
  at_risk: { bg: "rgba(249,115,22,0.08)", border: "rgba(249,115,22,0.15)", color: "#FB923C", label: "At Risk" },
  breached: { bg: "rgba(190,52,85,0.08)", border: "rgba(190,52,85,0.15)", color: "#BE3455", label: "Breached" },
};

function SLOCard({ slo, delay }) {
  const st = statusStyles[slo.status] || statusStyles.ok;
  const pct = slo.current_pct || 0;
  const target = slo.target_pct || 99;
  const budgetPct = slo.budget_remaining_pct || 0;
  const budgetColor = budgetPct > 50 ? "#4ADE80" : budgetPct > 20 ? "#FBBF24" : budgetPct > 0 ? "#FB923C" : "#F87171";

  return (
    <div className="sf-card p-5 animate-fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={`slo-${slo.slo_id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: st.bg }}>
            <Target className="w-4 h-4" style={{ color: st.color }} />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-[#2D3142]">{slo.name}</h3>
            <p className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{slo.pipeline_id} / {slo.metric} / {slo.window_days}d window</p>
          </div>
        </div>
        <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: st.bg, color: st.color }}>{st.label}</Badge>
      </div>

      {/* Main metric */}
      <div className="flex items-end gap-1 mb-4">
        <span className="text-[36px] font-extrabold tracking-tight text-[#2D3142] leading-none">{pct.toFixed(1)}</span>
        <span className="text-[14px] font-semibold mb-1" style={{ color: "#6B7280" }}>%</span>
        <span className="text-[12px] ml-2 mb-1" style={{ color: "#6B7280" }}>/ {target}% target</span>
      </div>

      {/* Progress bar */}
      <div className="relative mb-3">
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(45,49,66,0.04)" }}>
          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(pct, 100)}%`, background: `linear-gradient(90deg, ${st.color}, ${st.color}90)` }} />
        </div>
        <div className="absolute top-0 h-2" style={{ left: `${target}%`, width: "2px", background: "white", opacity: 0.5 }} />
      </div>

      {/* Budget */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: "#6B7280" }}>Error Budget</span>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(45,49,66,0.04)" }}>
              <div className="h-full rounded-full" style={{ width: `${budgetPct}%`, background: budgetColor }} />
            </div>
            <span className="text-[11px] font-mono font-bold" style={{ color: budgetColor }}>{budgetPct}%</span>
          </div>
        </div>
        <span className="text-[10px] font-mono" style={{ color: "#9CA3AF" }}>{slo.created_at?.slice(0, 10)}</span>
      </div>
    </div>
  );
}

export default function SLOsPage() {
  const [slos, setSlos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/slos").then((r) => { setSlos(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="p-6" data-testid="slos-loading"><Skeleton className="h-8 w-48 bg-white/5" /></div>;

  const breached = slos.filter((s) => s.status === "breached").length;
  const atRisk = slos.filter((s) => s.status === "at_risk" || s.status === "warning").length;

  return (
    <div className="p-6 space-y-6" data-testid="slos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Service Level Objectives</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{slos.length} SLOs / {breached} breached / {atRisk} at risk</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="slos-grid">
        {slos.map((slo, i) => <SLOCard key={slo.slo_id} slo={slo} delay={i * 80} />)}
      </div>
    </div>
  );
}
